import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
  RECEIPT_ACCEPTED_MIME_TYPES,
  RECEIPT_ACCEPT_ATTRIBUTE,
  RECEIPT_MAX_SIZE_BYTES,
} from '../../constants/expenses.constants';
import {
  EXPENSE_CATEGORIES,
  ExpenseCategory,
} from '../../enums/expense-category.enum';
import {
  EXPENSE_STATUSES,
  ExpenseStatus,
  keepsFreeText,
  requiresFreeText,
  takesReturnReason,
} from '../../enums/expense-status.enum';
import {
  EXPENSE_RETURN_REASONS,
  ExpenseReturnReason,
} from '../../enums/expense-return-reason.enum';
import { EXPENSE_RETURN_REASON_LABEL } from '../../constants/expense-return-reason.map';
import { expenseErrorMessageKey } from '../../data/expense-error';
import { Expense, ExpenseDriverOption } from '../../models/expense.model';
import { nodeIdValidator, normalizeNodeId } from '../../utils/node-id.util';
import {
  licensePlateValidator,
  normalizeLicensePlate,
} from '../../utils/license-plate.util';
import { formatAmountInput, parseAmountInput } from '../../utils/money.util';
import { finalize, Observable } from 'rxjs';
import {
  ExpenseFormMode,
  ExpenseFormResult,
  ExpenseFormSubmit,
  ExpenseReceiptIntent,
} from './expense-form-sidebar.model';

interface ExpenseFormControls {
  /** Servicio al que se imputa el gasto. Obligatorio en la API, sólo en alta. */
  nodeId: FormControl<string>;
  /**
   * Nulable a propósito: con cadena vacía `ng-select` marca el control como
   * `ng-has-value`, oculta el placeholder y pinta una etiqueta vacía con su «×»,
   * que es lo que hacía que el campo se viera en blanco y roto.
   */
  driverId: FormControl<string | null>;
  licensePlate: FormControl<string>;
  category: FormControl<ExpenseCategory>;
  description: FormControl<string>;
  /**
   * Texto, no número: el diseño muestra el importe en formato español
   * (`12,30`), que un `input[type=number]` no puede representar.
   */
  amount: FormControl<string>;
  status: FormControl<ExpenseStatus>;
  reason: FormControl<string>;
  /**
   * Motivo del catálogo, sólo al devolver. Nulable para que `select` arranque sin
   * preselección: con el primero elegido, devolver sin mirar mandaría un motivo
   * que no es.
   */
  reasonCode: FormControl<ExpenseReturnReason | null>;
}

const DESCRIPTION_MAX_LENGTH = 250;
const REASON_MAX_LENGTH = 250;

/** Valida que el texto del importe se pueda interpretar y sea mayor que cero. */
function amountValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;

  if (typeof raw !== 'string' || raw.trim() === '') {
    return null; // La obligatoriedad la cubre Validators.required.
  }

  const cents = parseAmountInput(raw);

  if (cents === null) {
    return { amountFormat: true };
  }

  return cents > 0 ? null : { amountMin: true };
}

/**
 * Panel lateral de alta y edición de un gasto.
 *
 * Un único componente para los dos modos: los campos que difieren son pocos y
 * la diferencia es declarativa (en alta se piden conductor y matrícula; en
 * edición se muestran conductor y order hash bloqueados). Dos componentes al 90 %
 * idénticos son la forma en que nacen los duplicados.
 *
 * No accede a datos: recibe los conductores por `@Input` y devuelve el resultado
 * al contenedor, que se encarga de subir el comprobante y persistir.
 */
@Component({
  selector: 'app-expense-form-sidebar',
  templateUrl: './expense-form-sidebar.component.html',
  styleUrls: ['../expense-panel.css', './expense-form-sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpenseFormSidebarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() mode: ExpenseFormMode = 'create';

  /** Gasto a editar. Obligatorio en modo `edit`. */
  @Input() expense: Expense | null = null;

  /**
   * Origen de los conductores del buscador (sólo en alta).
   *
   * Se resuelve dentro del panel para poder abrirlo al instante y mostrar el
   * loader en el propio selector, en lugar de dejar al usuario esperando con la
   * pantalla quieta antes de que aparezca el formulario.
   */
  @Input() driversLoader: Observable<readonly ExpenseDriverOption[]> | null =
    null;

  /**
   * Persiste el gasto y devuelve lo guardado.
   *
   * El guardado se ejecuta con el panel abierto a propósito: cerrarlo antes y
   * dejar que el contenedor guardara hacía perder todo lo escrito si la API
   * respondía 400 o 404. Ahora el panel sólo se cierra cuando el guardado
   * termina bien, y si falla el error se muestra aquí dentro.
   */
  @Input() submitHandler: ExpenseFormSubmit | null = null;

  protected isSaving = false;
  protected saveError: string | null = null;

  protected drivers: readonly ExpenseDriverOption[] = [];
  protected isLoadingDrivers = false;
  protected hasDriversError = false;

  protected readonly categories = EXPENSE_CATEGORIES;
  protected readonly statuses = EXPENSE_STATUSES;
  protected readonly descriptionMaxLength = DESCRIPTION_MAX_LENGTH;
  protected readonly reasonMaxLength = REASON_MAX_LENGTH;
  protected readonly acceptAttribute = RECEIPT_ACCEPT_ATTRIBUTE;

  /**
   * Tamaño máximo legible para el mensaje de error, derivado de la constante:
   * si cambia el límite, el mensaje no se queda mintiendo.
   */
  protected readonly maxSizeLabel = `${Math.round(
    RECEIPT_MAX_SIZE_BYTES / (1024 * 1024)
  )} MB`;

  protected readonly form: FormGroup<ExpenseFormControls>;

  /** Comprobante que ya tenía el gasto y sigue vigente. */
  protected existingReceiptUrl: string | null = null;

  /** Previsualización local del fichero recién elegido (nunca se sube aquí). */
  protected newReceiptPreviewUrl: string | null = null;
  protected newReceiptFileName: string | null = null;
  protected receiptError: string | null = null;

  /** Muestra la confirmación en el pie cuando se descarta con cambios. */
  protected isConfirmingDiscard = false;

  private receiptIntent: ExpenseReceiptIntent = { kind: 'unchanged' };
  private objectUrl: string | null = null;

  /** Primer campo editable: nodo en alta, categoría en edición. */
  @ViewChild('firstField') private firstField?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly fb: FormBuilder,
    private readonly activeOffcanvas: NgbActiveOffcanvas,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group<ExpenseFormControls>({
      nodeId: this.fb.nonNullable.control('', [
        Validators.required,
        nodeIdValidator(),
      ]),
      driverId: this.fb.control<string | null>(null, Validators.required),
      licensePlate: this.fb.nonNullable.control('', [
        Validators.required,
        licensePlateValidator(),
      ]),
      // La categoría es un slug abierto: se arranca con la primera conocida.
      category: this.fb.nonNullable.control(
        EXPENSE_CATEGORIES[0],
        Validators.required
      ),
      description: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.maxLength(DESCRIPTION_MAX_LENGTH),
      ]),
      amount: this.fb.nonNullable.control('', [
        Validators.required,
        amountValidator,
      ]),
      status: this.fb.nonNullable.control(
        ExpenseStatus.Pending,
        Validators.required
      ),
      reason: this.fb.nonNullable.control('', [
        Validators.maxLength(REASON_MAX_LENGTH),
      ]),
      reasonCode: this.fb.control<ExpenseReturnReason | null>(null),
    });
  }

  ngOnInit(): void {
    if (this.mode === 'edit' && this.expense) {
      this.hydrateFromExpense(this.expense);
    }

    // El motivo sólo es obligatorio —y sólo se muestra— al rechazar.
    this.form.controls.status.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => this.syncReasonValidators(status));

    this.syncReasonValidators(this.form.controls.status.value);

    if (!this.isEditMode) {
      this.loadDrivers();
    }
  }

  private loadDrivers(): void {
    if (!this.driversLoader) {
      return;
    }

    this.isLoadingDrivers = true;
    this.hasDriversError = false;
    this.cdr.markForCheck();

    this.driversLoader
      .pipe(
        finalize(() => {
          this.isLoadingDrivers = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (drivers) => {
          this.drivers = drivers;
          this.cdr.markForCheck();
        },
        error: () => {
          this.drivers = [];
          this.hasDriversError = true;
          this.cdr.markForCheck();
        },
      });
  }

  protected retryDrivers(): void {
    this.loadDrivers();
  }

  ngAfterViewInit(): void {
    // Abrir un panel y tener que buscar dónde escribir es fricción gratuita.
    this.firstField?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.revokeObjectUrl();
  }

  protected get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  /** Las dos decisiones negativas conservan el texto; sólo una lo exige. */
  protected get needsReason(): boolean {
    return keepsFreeText(this.form.controls.status.value);
  }

  /** El asterisco sigue al validador en lugar de estar escrito a mano. */
  protected get isReasonRequired(): boolean {
    return requiresFreeText(this.form.controls.status.value);
  }

  /** Devolver elige del catálogo; rechazar no lo tiene y escribe. */
  protected get needsReturnReason(): boolean {
    return takesReturnReason(this.form.controls.status.value);
  }

  protected readonly returnReasons = EXPENSE_RETURN_REASONS;
  protected readonly returnReasonLabels = EXPENSE_RETURN_REASON_LABEL;


  protected get reasonPlaceholderKey(): string {
    return this.isEditMode
      ? 'expenses.form.rejection_reason_placeholder_edit'
      : 'expenses.form.rejection_reason_placeholder_create';
  }

  protected get hasReceipt(): boolean {
    return Boolean(this.newReceiptPreviewUrl ?? this.existingReceiptUrl);
  }

  protected get receiptPreviewUrl(): string | null {
    return this.newReceiptPreviewUrl ?? this.existingReceiptUrl;
  }

  protected get descriptionLength(): number {
    return this.form.controls.description.value.length;
  }

  protected get reasonLength(): number {
    return this.form.controls.reason.value.length;
  }

  protected get hasPendingChanges(): boolean {
    return this.form.dirty || this.receiptIntent.kind !== 'unchanged';
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    // Permite volver a elegir el mismo fichero tras un error.
    input.value = '';

    if (!file) {
      return;
    }

    if (!RECEIPT_ACCEPTED_MIME_TYPES.includes(file.type)) {
      this.receiptError = 'expenses.form.file_type_invalid';
      this.cdr.markForCheck();
      return;
    }

    if (file.size > RECEIPT_MAX_SIZE_BYTES) {
      this.receiptError = 'expenses.form.file_too_large';
      this.cdr.markForCheck();
      return;
    }

    this.revokeObjectUrl();

    this.receiptError = null;
    this.receiptIntent = { kind: 'new', file };
    this.newReceiptFileName = file.name;

    // Previsualización local: los PDF no se pintan, se muestran por nombre.
    if (file.type === 'application/pdf') {
      this.newReceiptPreviewUrl = null;
    } else {
      this.objectUrl = URL.createObjectURL(file);
      this.newReceiptPreviewUrl = this.objectUrl;
    }

    this.cdr.markForCheck();
  }

  protected removeReceipt(): void {
    this.revokeObjectUrl();

    this.newReceiptPreviewUrl = null;
    this.newReceiptFileName = null;
    this.existingReceiptUrl = null;
    this.receiptError = null;
    this.receiptIntent = { kind: 'removed' };

    this.cdr.markForCheck();
  }

  protected submit(): void {
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      this.focusFirstError();
      return;
    }

    const result = this.buildResult();

    // Sin handler el panel se limita a devolver el resultado: es lo que usan
    // los tests que sólo comprueban el payload.
    if (!this.submitHandler) {
      this.activeOffcanvas.close(result);
      return;
    }

    this.isSaving = true;
    this.saveError = null;
    this.cdr.markForCheck();

    this.submitHandler(result)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (saved) => this.activeOffcanvas.close(saved),
        error: (error: unknown) => {
          this.saveError = this.messageFor(error);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * El backend devuelve el motivo como código (`{"Error":"InvalidAmount"}`) y el
   * texto lo pone el front. Se muestra junto al formulario, con el panel abierto,
   * para no perder lo escrito.
   */
  private messageFor(error: unknown): string {
    return expenseErrorMessageKey(error) ?? 'expenses.errors.unknown';
  }

  /** Lleva el foco al primer campo con error para no dejar al usuario buscando. */
  private focusFirstError(): void {
    const invalid = document.querySelector<HTMLElement>(
      '.expenses-offcanvas .expense-field__input.ng-invalid,' +
        ' .expenses-offcanvas .expense-field__textarea.ng-invalid'
    );

    invalid?.focus();
  }

  protected requestDiscard(): void {
    if (!this.hasPendingChanges) {
      this.activeOffcanvas.dismiss();
      return;
    }

    this.isConfirmingDiscard = true;
    // Explícito y no redundante: al invocarse desde el template el evento ya
    // marca la vista, pero así el componente es correcto también si se llama
    // desde código.
    this.cdr.markForCheck();
  }

  protected confirmDiscard(): void {
    this.activeOffcanvas.dismiss();
  }

  protected cancelDiscard(): void {
    this.isConfirmingDiscard = false;
    this.cdr.markForCheck();
  }

  protected close(): void {
    this.requestDiscard();
  }

  /** Normaliza la matrícula al salir del campo (`1234-abc` → `1234-ABC`). */
  protected onLicensePlateBlur(): void {
    const control = this.form.controls.licensePlate;
    const normalized = normalizeLicensePlate(control.value);

    if (normalized !== control.value) {
      control.setValue(normalized);
    }
  }

  /** Normaliza el identificador de nodo al salir del campo. */
  protected onNodeIdBlur(): void {
    const control = this.form.controls.nodeId;
    const normalized = normalizeNodeId(control.value);

    if (normalized !== control.value) {
      control.setValue(normalized);
    }
  }

  /** Al entrar, selecciona el importe entero para poder teclear encima. */
  protected onAmountFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  /** Al salir del campo, reescribe el importe con dos decimales y coma. */
  protected onAmountBlur(): void {
    const control = this.form.controls.amount;
    const cents = parseAmountInput(control.value);

    if (cents === null) {
      return;
    }

    const formatted = formatAmountInput(cents);

    if (formatted !== control.value) {
      control.setValue(formatted);
    }
  }

  private syncReasonValidators(status: ExpenseStatus): void {
    const control = this.form.controls.reason;

    if (requiresFreeText(status)) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }

    /*
     * Se vacía sólo cuando el campo DESAPARECE, no cuando deja de ser
     * obligatorio. Desde que devolver pide el código y no el texto, hay un estado
     * en el que el campo sigue a la vista sin ser obligatorio, y limpiarlo ahí
     * borraba el motivo de un gasto ya devuelto nada más abrirlo para editarlo.
     */
    if (!keepsFreeText(status)) {
      control.setValue('');
    }

    const codeControl = this.form.controls.reasonCode;

    if (takesReturnReason(status)) {
      codeControl.addValidators(Validators.required);
    } else {
      codeControl.removeValidators(Validators.required);
      // Deja de aplicar: no puede seguir viajando ni bloqueando el guardado.
      codeControl.setValue(null);
    }

    codeControl.updateValueAndValidity({ emitEvent: false });

    control.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private hydrateFromExpense(expense: Expense): void {
    // El dominio tiene estos campos nulables (histórico); los controles no.
    this.form.patchValue({
      nodeId: expense.nodeId,
      driverId: expense.driverId,
      licensePlate: expense.licensePlate ?? '',
      category: expense.category,
      description: expense.description ?? '',
      amount: formatAmountInput(expense.amountInCents),
      status: expense.status ?? ExpenseStatus.Pending,
      reason: expense.review?.rejectionReason ?? '',
      reasonCode: expense.review?.returnReasonCode ?? null,
    });

    // En edición el conductor, la matrícula y el servicio no se pueden cambiar:
    // se muestran bloqueados y quedan fuera del payload de actualización.
    this.form.controls.nodeId.disable();
    this.form.controls.driverId.disable();
    this.form.controls.licensePlate.disable();

    this.existingReceiptUrl = expense.receiptUrl;
  }

  private buildResult(): ExpenseFormResult {
    const raw = this.form.getRawValue();

    return {
      mode: this.mode,
      values: {
        category: raw.category,
        description: raw.description.trim(),
        amountInCents: parseAmountInput(raw.amount) ?? 0,
        status: raw.status,
        reason: keepsFreeText(raw.status) ? raw.reason.trim() || null : null,
        reasonCode: takesReturnReason(raw.status) ? raw.reasonCode : null,
        ...(this.isEditMode
          ? {}
          : {
              nodeId: normalizeNodeId(raw.nodeId),
              driverId: raw.driverId ?? '',
              licensePlate: normalizeLicensePlate(raw.licensePlate),
            }),
      },
      receipt: this.receiptIntent,
    };
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
